/* src/modules/peer/PeerAppreciation.js */
import React, { useState, useEffect, useRef } from 'react';
// --- ADDED: getDoc to imports ---
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import confetti from 'canvas-confetti';

import AppreciationWall from './AppreciationWall';
import AppreciationDashboard from './AppreciationDashboard';
import ChallengesHub from './ChallengesHub';

const BRAND_COLORS = {
    teal: '#20BEC6',
    orange: '#F4A866',
    darkBlue: '#213368'
};

export default function PeerAppreciation({ userProfile, appId }) {
    const [activeTab, setActiveTab] = useState('wall');
    const [hasNewItems, setHasNewItems] = useState(false);
    
    // Lock to ensure we only run this logic once per mount
    const checkPerformed = useRef(false);

    useEffect(() => {
        const checkForNewAppreciations = async () => {
            if (!userProfile?.id || checkPerformed.current) return;
            
            // Lock immediately
            checkPerformed.current = true;
            const myId = userProfile.uid || userProfile.id;

            try {
                // 1. FETCH FRESH USER DATA
                // We do not rely on the 'userProfile' prop here because it might be stale 
                // if the user navigated away and back without a full page refresh.
                const userRef = doc(db, `artifacts/${appId}/users`, myId);
                const userSnap = await getDoc(userRef);
                
                let lastCheckDate = new Date(0); // Default Epoch

                if (userSnap.exists()) {
                    const data = userSnap.data();
                    if (data.lastAppreciationCheck) {
                        lastCheckDate = data.lastAppreciationCheck.toDate();
                    }
                }

                // 2. QUERY FOR NEW ITEMS
                const appRef = collection(db, `artifacts/${appId}/appreciations`);
                const q = query(
                    appRef, 
                    where('toUserId', '==', myId),
                    where('createdAt', '>', lastCheckDate)
                );

                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    // Found new items!
                    
                    // A. Trigger Confetti
                    confetti({
                        particleCount: 150,
                        spread: 70,
                        origin: { y: 0.6 },
                        colors: [BRAND_COLORS.teal, BRAND_COLORS.orange, BRAND_COLORS.darkBlue]
                    });

                    // B. Show notification badge
                    setHasNewItems(true);

                    // C. Update timestamp immediately
                    await updateDoc(userRef, {
                        lastAppreciationCheck: serverTimestamp()
                    });
                }
            } catch (error) {
                console.error("Error checking for new appreciations:", error);
            }
        };

        checkForNewAppreciations();
        
    }, [appId, userProfile?.id, userProfile?.uid]); // Reduced dependencies

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        if (tabId === 'dashboard') {
            setHasNewItems(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-[#213368]">Peer Appreciation</h2>
                    <p className="text-sm text-gray-500">Recognize greatness and fuel success.</p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-1 border-b border-gray-200">
                <TabButton 
                    id="wall" 
                    label="The Wall" 
                    activeTab={activeTab} 
                    setActiveTab={handleTabChange} 
                />
                
                {/* Dashboard Tab with Notification Badge */}
                <div className="relative">
                    <TabButton 
                        id="dashboard" 
                        label="My Dashboard" 
                        activeTab={activeTab} 
                        setActiveTab={handleTabChange} 
                    />
                    {hasNewItems && (
                        <span className="absolute top-2 right-2 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                        </span>
                    )}
                </div>

                <TabButton 
                    id="challenges" 
                    label="Leaderboard & Challenges" 
                    activeTab={activeTab} 
                    setActiveTab={handleTabChange} 
                />
            </div>

            {/* Content Area */}
            <div className="bg-white p-6 rounded-b-lg rounded-tr-lg shadow-sm border border-gray-200 min-h-[400px]">
                
                {activeTab === 'wall' && (
                    <AppreciationWall userProfile={userProfile} appId={appId} />
                )}
                
                {activeTab === 'dashboard' && (
                    <AppreciationDashboard userProfile={userProfile} appId={appId} />
                )}

                {activeTab === 'challenges' && (
                    <ChallengesHub userProfile={userProfile} appId={appId} />
                )}
            </div>
        </div>
    );
}

// Tab Button Helper
function TabButton({ id, label, activeTab, setActiveTab }) {
    const isActive = activeTab === id;
    return (
        <button
            onClick={() => setActiveTab(id)}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 rounded-t-lg ${
                isActive 
                    ? 'border-[#213368] text-[#213368] bg-blue-50' 
                    : 'border-transparent text-gray-500 hover:text-[#213368] hover:bg-gray-50'
            }`}
        >
            {label}
        </button>
    );
}